/**
 * Supplier risk assessments (the 5×5 likelihood × consequence matrix).
 * Backend: `suppliers/views_risk.py` — `/api/v1/suppliers/risk-*`.
 */
import api, { type AxiosResponse, type PaginatedResponse } from './client';

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type RiskAssessmentStatus = 'draft' | 'in_review' | 'completed' | 'requires_action';

export interface RiskCategory {
  id: number;
  name: string;
  description: string;
  category_type: string;
  weight: string;
  is_active: boolean;
}

export interface RiskCategoryScore {
  id: number;
  assessment: number;
  category: number;
  category_name?: string;
  likelihood: number;
  consequence: number;
  risk_score: string;
  risk_level: RiskLevel;
  risk_description: string;
  current_controls: string;
  additional_actions_required: string;
}

export interface RiskAssessment {
  id: number;
  supplier: number;
  supplier_name?: string;
  customer: number;
  overall_risk_level: RiskLevel;
  overall_risk_score: string;
  status: RiskAssessmentStatus;
  assessment_date: string;
  next_review_date: string | null;
  assessed_by: number | null;
  assessed_by_name?: string;
  reviewed_by: number | null;
  summary: string;
  recommendations: string;
  internal_notes: string;
  category_risks?: RiskCategoryScore[];
  created_at: string;
  updated_at: string;
}

export interface RiskMatrixCell {
  category: string;
  category_type: string;
  likelihood: number;
  consequence: number;
  risk_level: RiskLevel;
  risk_score: number;
}

export interface RiskMatrixData {
  assessment_id: number;
  assessment_date: string;
  overall_risk_level: RiskLevel;
  overall_risk_score: number;
  matrix_data: RiskMatrixCell[];
}

export interface RiskAssessmentPayload {
  supplier: number;
  customer: number;
  status: RiskAssessmentStatus;
  assessment_date: string;
  next_review_date: string | null;
  summary: string;
  recommendations: string;
  overall_risk_level: RiskLevel;
  assessed_by?: number | null;
}

export interface RiskCategoryScorePayload {
  assessment: number;
  category: number;
  likelihood: number;
  consequence: number;
  risk_description?: string;
  current_controls?: string;
  additional_actions_required?: string;
}

export const riskAPI = {
  categories: (): Promise<AxiosResponse<PaginatedResponse<RiskCategory> | RiskCategory[]>> =>
    api.get('/suppliers/risk-categories/', { params: { is_active: true, page_size: 100 } }),
  assessments: (supplierId: number): Promise<AxiosResponse<PaginatedResponse<RiskAssessment> | RiskAssessment[]>> =>
    api.get('/suppliers/risk-assessments/', { params: { supplier: supplierId, ordering: '-assessment_date', page_size: 50 } }),
  assessment: (id: number): Promise<AxiosResponse<RiskAssessment>> => api.get(`/suppliers/risk-assessments/${id}/`),
  createAssessment: (data: RiskAssessmentPayload): Promise<AxiosResponse<RiskAssessment>> =>
    api.post('/suppliers/risk-assessments/', data),
  updateAssessment: (id: number, data: Partial<RiskAssessmentPayload>): Promise<AxiosResponse<RiskAssessment>> =>
    api.patch(`/suppliers/risk-assessments/${id}/`, data),
  deleteAssessment: (id: number): Promise<AxiosResponse<void>> => api.delete(`/suppliers/risk-assessments/${id}/`),
  createScore: (data: RiskCategoryScorePayload): Promise<AxiosResponse<RiskCategoryScore>> =>
    api.post('/suppliers/risk-scores/', data),
  updateScore: (id: number, data: Partial<RiskCategoryScorePayload>): Promise<AxiosResponse<RiskCategoryScore>> =>
    api.patch(`/suppliers/risk-scores/${id}/`, data),
  matrix: (supplierId: number): Promise<AxiosResponse<RiskMatrixData>> =>
    api.get('/suppliers/risk-assessments/risk_matrix_data/', { params: { supplier: supplierId } }),
};
